import { useState } from 'react';
import { useSuppliers } from '../../hooks/useSuppliers';
import { Supplier, SupplierCategory, SupplierStatus, categoryConfig, statusConfig } from '../../mocks/suppliers';
import SupplierCard from './components/SupplierCard';
import SupplierDetailModal from './components/SupplierDetailModal';
import AddSupplierModal from './components/AddSupplierModal';

type CategoryFilter = SupplierCategory | 'All';

const CATEGORY_FILTERS: { label: string; value: CategoryFilter; icon: string }[] = [
  { label: 'All Suppliers', value: 'All', icon: 'ri-building-4-line' },
  { label: 'Hotels & Resorts', value: 'Hotel & Resort', icon: 'ri-hotel-line' },
  { label: 'Airlines', value: 'Airline', icon: 'ri-flight-takeoff-line' },
  { label: 'DMC & Tour Ops', value: 'DMC & Tour Operator', icon: 'ri-map-2-line' },
  { label: 'Visa Services', value: 'Visa Service', icon: 'ri-passport-line' },
  { label: 'Cruise Lines', value: 'Cruise Line', icon: 'ri-ship-line' },
  { label: 'Safari & Adventure', value: 'Safari & Adventure', icon: 'ri-tree-line' },
];

export default function SuppliersPage() {
  const { suppliers, loading, createSupplier, updateSupplier } = useSuppliers();
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('All');
  const [statusFilter, setStatusFilter] = useState<SupplierStatus | 'All'>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [editSupplier, setEditSupplier] = useState<Supplier | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'revenue' | 'bookings' | 'rating'>('revenue');

  const filteredSuppliers = suppliers
    .filter((s) => categoryFilter === 'All' || s.category === categoryFilter)
    .filter((s) => statusFilter === 'All' || s.status === statusFilter)
    .filter(
      (s) =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.contacts.some((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'revenue') return b.totalSellValue - a.totalSellValue;
      if (sortBy === 'bookings') return b.totalBookings - a.totalBookings;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  const totalRevenue = suppliers.reduce((s, sup) => s + sup.totalSellValue, 0);
  const totalCost = suppliers.reduce((s, sup) => s + sup.totalCostValue, 0);
  const avgMargin = totalRevenue > 0 ? Math.round(((totalRevenue - totalCost) / totalRevenue) * 100) : 0;
  const preferredCount = suppliers.filter((s) => s.status === 'Preferred').length;
  const activeCount = suppliers.filter((s) => s.status === 'Active' || s.status === 'Preferred').length;
  const countriesCount = new Set(suppliers.map((s) => s.country)).size;

  const kpis = [
    { label: 'Total Suppliers', value: suppliers.length.toString(), icon: 'ri-building-4-line', color: 'bg-amber-100 text-amber-600' },
    { label: 'Active Suppliers', value: activeCount.toString(), sub: `${preferredCount} Preferred`, icon: 'ri-checkbox-circle-line', color: 'bg-emerald-100 text-emerald-600' },
    { label: 'Countries Covered', value: countriesCount.toString(), icon: 'ri-earth-line', color: 'bg-teal-100 text-teal-600' },
    { label: 'Total Booked Value', value: `AED ${(totalRevenue / 1000).toFixed(0)}k`, sub: `${avgMargin}% avg margin`, icon: 'ri-money-dollar-circle-line', color: 'bg-violet-100 text-violet-600' },
  ];

  const categoryCounts: Record<string, number> = { All: suppliers.length };
  suppliers.forEach((s) => {
    categoryCounts[s.category] = (categoryCounts[s.category] || 0) + 1;
  });

  const handleSaveSupplier = async (data: Partial<Supplier>) => {
    if (editSupplier) {
      await updateSupplier(editSupplier.id, data);
    } else {
      await createSupplier(data);
    }
    setAddModalOpen(false);
    setEditSupplier(null);
  };

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Supplier Directory</h1>
              <p className="text-sm text-gray-500 mt-0.5">Manage your hotels, airlines, DMCs, and service providers</p>
            </div>
            <button
              onClick={() => { setAddModalOpen(true); setEditSupplier(null); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base" /> Add Supplier
            </button>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-4">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${kpi.color} flex-shrink-0`}>
                  <i className={`${kpi.icon} text-xl`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-0.5">{kpi.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{kpi.value}</p>
                  {kpi.sub && <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Category Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORY_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setCategoryFilter(f.value)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer border ${
                  categoryFilter === f.value
                    ? 'bg-amber-100 text-amber-700 border-amber-200'
                    : 'bg-white text-gray-500 border-gray-100 hover:border-gray-200'
                }`}
              >
                <i className={`${f.icon} text-sm`} />
                {f.label}
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${categoryFilter === f.value ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {categoryCounts[f.value] ?? 0}
                </span>
              </button>
            ))}
          </div>

          {/* Search, Filter, Sort, View Toggle */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search suppliers, countries, contacts..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SupplierStatus | 'All')}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer text-gray-600"
            >
              <option value="All">All Statuses</option>
              <option value="Preferred">Preferred</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Blacklisted">Blacklisted</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer text-gray-600"
            >
              <option value="revenue">Sort by Revenue</option>
              <option value="bookings">Sort by Bookings</option>
              <option value="rating">Sort by Rating</option>
              <option value="name">Sort by Name</option>
            </select>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 ml-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`w-8 h-7 flex items-center justify-center rounded-md text-sm cursor-pointer transition-colors ${viewMode === 'grid' ? 'bg-white text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <i className="ri-grid-line" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`w-8 h-7 flex items-center justify-center rounded-md text-sm cursor-pointer transition-colors ${viewMode === 'table' ? 'bg-white text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <i className="ri-list-unordered" />
              </button>
            </div>
            <p className="text-sm text-gray-400 whitespace-nowrap">
              {filteredSuppliers.length} of {suppliers.length}
            </p>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            filteredSuppliers.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {filteredSuppliers.map((supplier) => (
                  <SupplierCard
                    key={supplier.id}
                    supplier={supplier}
                    onView={setSelectedSupplier}
                    onEdit={(s) => { setEditSupplier(s); setAddModalOpen(true); }}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 p-14 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-4-line text-2xl text-gray-300" />
                </div>
                <p className="text-gray-400">No suppliers match your search</p>
              </div>
            )
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 text-xs font-semibold text-gray-500 border-b border-gray-100">
                <div className="col-span-3">Supplier</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2">Country</div>
                <div className="col-span-1 text-center">Rating</div>
                <div className="col-span-1 text-center">Bookings</div>
                <div className="col-span-2 text-right">Revenue</div>
                <div className="col-span-1 text-right">Status</div>
              </div>
              {filteredSuppliers.map((s, i) => {
                const cat = categoryConfig[s.category];
                const st = statusConfig[s.status];
                return (
                  <div
                    key={s.id}
                    className={`grid grid-cols-12 px-4 py-3.5 items-center text-sm hover:bg-gray-50 cursor-pointer transition-colors ${i !== filteredSuppliers.length - 1 ? 'border-b border-gray-50' : ''}`}
                    onClick={() => setSelectedSupplier(s)}
                  >
                    <div className="col-span-3 flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                        style={{ backgroundColor: s.logoColor }}
                      >
                        {s.logo}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{s.name}</p>
                        <p className="text-xs text-gray-400">{s.contacts[0]?.name}</p>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.bg} ${cat.color}`}>
                        <i className={`${cat.icon} mr-1`} />{s.category.split(' ')[0]}
                      </span>
                    </div>
                    <div className="col-span-2 text-gray-600 text-sm">{s.city}, {s.country}</div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm font-semibold text-amber-500">{s.rating}</span>
                      <i className="ri-star-fill text-amber-400 text-xs ml-0.5" />
                    </div>
                    <div className="col-span-1 text-center font-semibold text-gray-700">{s.totalBookings}</div>
                    <div className="col-span-2 text-right">
                      <p className="font-semibold text-gray-800">AED {s.totalSellValue.toLocaleString()}</p>
                      <p className="text-xs text-emerald-600">{Math.round(((s.totalSellValue - s.totalCostValue) / (s.totalSellValue || 1)) * 100)}% margin</p>
                    </div>
                    <div className="col-span-1 text-right">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${st.bg} ${st.color}`}>
                        {s.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Top Suppliers Highlight */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Top Performers by Revenue</h3>
            <div className="space-y-3">
              {[...suppliers]
                .sort((a, b) => b.totalSellValue - a.totalSellValue)
                .slice(0, 5)
                .map((s, i) => {
                  const maxVal = Math.max(...suppliers.map((sup) => sup.totalSellValue));
                  const pct = (s.totalSellValue / maxVal) * 100;
                  const cat = categoryConfig[s.category];
                  return (
                    <div key={s.id} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                        style={{ backgroundColor: s.logoColor }}
                      >
                        {s.logo}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-800">{s.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${cat.bg} ${cat.color}`}>
                              <i className={`${cat.icon} mr-0.5`} />{s.category.split(' ')[0]}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-gray-700">AED {s.totalSellValue.toLocaleString()}</span>
                        </div>
                        <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${pct}%`, backgroundColor: s.logoColor }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedSupplier && (
        <SupplierDetailModal
          supplier={selectedSupplier}
          onClose={() => setSelectedSupplier(null)}
          onEdit={() => {
            setEditSupplier(selectedSupplier);
            setSelectedSupplier(null);
            setAddModalOpen(true);
          }}
        />
      )}
      {addModalOpen && (
        <AddSupplierModal
          initialData={editSupplier}
          onClose={() => { setAddModalOpen(false); setEditSupplier(null); }}
          onSave={handleSaveSupplier}
        />
      )}
    </div>
  );
}
